import React, { useEffect, useRef, useState } from "react";

/**
 * Animated number counter — spring-eases from 0 (or `from`) to `value`.
 * Respects prefers-reduced-motion (renders final value instantly).
 *
 *   <CountUp value={1234} duration={1.2} prefix="$" decimals={2} />
 */
export default function CountUp({
  value = 0,
  duration = 1.0,
  from = 0,
  prefix = "",
  suffix = "",
  decimals = 0,
  className = "",
  testId,
}) {
  const [display, setDisplay] = useState(from);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || duration <= 0) { setDisplay(value); return; }

    cancelAnimationFrame(rafRef.current);
    startRef.current = null;
    const start = from;
    const end = Number(value) || 0;
    const dur = duration * 1000;

    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / dur);
      // ease-out-quint — slow finish, snappy start
      const eased = 1 - Math.pow(1 - t, 5);
      setDisplay(start + (end - start) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else setDisplay(end);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  const formatted = Number(display).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span className={`tabular-nums ${className}`} data-testid={testId}>
      {prefix}{formatted}{suffix}
    </span>
  );
}
